import { TabContext, TabPanel } from "@mui/lab";
import FolderOffIcon from '@mui/icons-material/FolderOff';
import ContentPasteOffIcon from '@mui/icons-material/ContentPasteOff';
import DocumentScannerIcon from '@mui/icons-material/DocumentScanner';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Badge, Box, Button, Card, CardContent, CardHeader, CircularProgress, Divider, Grid, IconButton, List, 
  ListItem, ListItemButton, ListItemText, Stack, Tab, Tabs, Tooltip, Typography } from "@mui/material";
import { ResourceComponentPropsWithId, useEditContext, useRecordContext } from "ra-core";
import { FC, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router";
import { Content } from "@/components/content/Content";
import Edit from "@/controller/Edit";
import { RecPatch } from "@/dataProvider/tools";
import { ScanJobProgressReportDto } from "@/gen/api/securefile";
import { base64UrlDecode } from "@/utils/base64";
import { useUserTimeFormat } from "@/utils/userTimeFormat";
import WithSave from "@/controller/WithSave";
import { useDefaultSavedHandlers } from "@/controller/defaultSavedHandlers";
import numeral from "numeral";
import Files from "@/icons/Files";



const DMRootFolderScanInfo: FC<ResourceComponentPropsWithId> = (props) => {
  const { match, resource } = props
  const id = match ? (match.params as any)['clientId'] : ''
  const { search } = useLocation();
  const {machine, stateParams} = useMemo(() => {
    const qparams = new URLSearchParams(search)
    const bag = {
      machine: '',
      stateParams: null,
    }
    try {
      bag.stateParams = qparams.get('stateParams') && JSON.parse(base64UrlDecode(
        qparams.get('stateParams')!
      ))
    } catch { }
    
    try {
      if (qparams.get('machine')) {
        bag.machine = base64UrlDecode(
          qparams.get('machine')!
        )
      }
    } catch { }
    return bag  
  }, [search]);

  const saveHandler = useDefaultSavedHandlers("Request for Refresh")
  return (
    <Edit resource={resource} basePath="" id={id} mutationMode="pessimistic" {...saveHandler}>
      <DMRootFolderScanInfoBody {...props} id={id as any} machine={machine ?? ''} stateParams={stateParams} />
    </Edit>
  )
}

const DMRootFolderScanInfoBody: FC<ResourceComponentPropsWithId&{machine:string, stateParams:any}> = ({
  id, machine, options, stateParams,
}) => {
  const filter = stateParams
  const { loading, loaded, refetch } = useEditContext()
  const data = useRecordContext<ScanJobProgressReportDto&RecPatch>()
  const {
    inaccessibleFiles,
    inaccessibleFolders,
    filesScannedCount,
    rootFolders,
  } = useMemo(() => {
    const inaccessibleFiles = JSON.parse(data?.inaccessibleFiles ?? '{}') as Record<string, GroupInfo>
    const inaccessibleFolders = JSON.parse(data?.inaccessibleFolders ?? '{}') as Record<string, GroupInfo>
    const filesScannedCount = JSON.parse(data?.filesScannedCount ?? '{}') as Record<string, number>
    const endSlash = /[\\/]$/
    const rootFolders = Object.entries(
      [inaccessibleFolders as Record<string, any>, inaccessibleFiles, filesScannedCount].reduce((acc, bag) => {
        for (const s of Object.keys(bag)) {
          const k = s.replace(endSlash, '').toLowerCase()
          if (!acc[k]) { acc[k] = s }
          else if (acc[k] !== s) {
            const s0 = acc[k]
            bag[s0] = bag[s]
            delete bag[s]
          }
        }
        return acc
      }, {} as Record<string, string>)).sort((a, b) => a[0] < b[0] ? -1 : 1)
      .map(x => x[1])
    return {
      inaccessibleFiles,
      inaccessibleFolders,
      filesScannedCount,
      rootFolders,
    }
  }, [data?.filesScannedCount, data?.inaccessibleFiles, data?.inaccessibleFolders])

  const [expansion, setExpansion] = useState({
    dic: {} as Record<string, 'folder'|'file'>,
  })
  const [states, setStates] = useState({
    lastRefreshUpdate: null as string|null
  })
  const refresh = (triggerSucceed: boolean) => {
    if (!refetch) { return }
    if (!triggerSucceed) {
      refetch()
      return
    }
    setStates(v => {
      const lastRefreshUpdate = data.countUpdatedAt ?? (new Date()).toISOString()
      if (!v.lastRefreshUpdate) {
        setTimeout(() => refetch())
      }
      return {...v, lastRefreshUpdate}
    })
  }
  useEffect(() => {
    let timer = null
    if (states.lastRefreshUpdate && (!data?.countUpdatedAt || data.countUpdatedAt === states.lastRefreshUpdate)) {
      timer = window.setInterval(() => {
        refetch && refetch()
      }, 5000)
    } else {
      setStates(v => {
        return v.lastRefreshUpdate ? {...v, lastRefreshUpdate:null} : v
      })
    }
    return () => {
      if (timer) {
        window.clearInterval(timer)
      }
    }
  }, [data?.countUpdatedAt, refetch, states.lastRefreshUpdate])
  
  const format = useUserTimeFormat()

  if (!loaded) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', height: '60%', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    )
  }
  if (!data.machine && !data.latestScanStart) {
    return (
      <Content
        pageName="DataManager Root Folder Scan Info" 
        resourceName="Scan Info" 
        breadcrumbs={options?.breadcrumbs && [...options.breadcrumbs, { 
          label: 'Data Manager Reports', 
          path: `/secure-files/data-manager-reports`, 
          locationDescriptor: { pathname: '/secure-files/data-manager-reports', state: filter }
        }]}
      >
        <Grid container spacing={4}>
          <Grid item xs={12} >
            <Card>
              <CardHeader title={machine} />
              <Divider />
              <CardContent>
                <Typography>No Data to Display</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Content>
    )
  }
  return (
    <Content
      pageName="DataManager Root Folder Scan Info" 
      resourceName="Scan Info" 
      breadcrumbs={options?.breadcrumbs && [...options.breadcrumbs, { 
        label: 'Data Manager Reports', 
        path: `/secure-files/data-manager-reports`, 
        locationDescriptor: { pathname: '/secure-files/data-manager-reports', state: filter }
      }]}
    >
      <Grid container spacing={4}>
        <Grid item xs={12} >
          <Card>
            <CardHeader title={machine} />
            <Divider />
            <CardContent>
              <Stack direction="row" justifyContent="space-between" marginBottom={2}>
                <Box>
                  <Typography variant="subtitle2">Scan Job Duration</Typography>
                  <Box>
                    {data?.latestScanStart
                    ? <>
                        <Typography variant="caption">{format(data?.latestScanStart ?? 0, 'PPp')}</Typography>{" - "}
                        {data?.isDone 
                          ? <Typography variant="caption">{format(data?.lastScanEnd ?? 0, 'PPp')}</Typography>
                          : <Typography variant="caption" sx={{ color: 'secondary.light' }}>In Progress</Typography>}
                      </>
                    : <Typography variant="caption" sx={{ color: 'secondary.light' }}>N/A</Typography>}
                  </Box>
                </Box>
                <Box>
                  <Typography variant="subtitle2">Last Refreshed Time</Typography>
                  <Stack direction="row">
                    {data.countUpdatedAt
                      ? <Typography variant="caption" color={loading || states.lastRefreshUpdate ? 'InactiveCaptionText' : undefined}>
                          {format(data?.countUpdatedAt ?? 0, 'PPp')}
                        </Typography>
                      : <Typography variant="caption" sx={{ color: 'secondary.light' }}>N/A</Typography>}
                    <Box sx={{ ml: 2, my: -0.75 }}>
                      {!states.lastRefreshUpdate && !loading
                      ? <WithSave render={({save}) =>
                          <Tooltip title="Request for Refresh">
                            <IconButton size="small" color="primary" onClick={() => save({machine: data.machine}, { onSuccess: ({data}:any) => refresh(data?.data) })}>
                              <RefreshIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>}
                        />
                      : <CircularProgress size={18} sx={{ mt: 0.75, ml: 1.5 }} />}
                    </Box>
                  </Stack>
                </Box>
              </Stack>
              <Box sx={{ mx: -2, bgcolor: 'action.hover' }}>
                <Divider />
                  <Typography variant="subtitle1" marginLeft={2} marginY={1}>Root Folders</Typography>
                <Divider />
              </Box>
              <Box sx={{
                '& div.MuiTabPanel-root': { 
                  pt: 'unset', position: 'relative',
                  '& .MuiTypography-caption': {position: 'absolute', bottom: 0}
                }
              }}>
                <List>
                  {rootFolders.map(rootFolder => {
                    const tabVal = expansion.dic[rootFolder]
                    const g1 = inaccessibleFolders[rootFolder]
                    const g2 = inaccessibleFiles[rootFolder]
                    const n = filesScannedCount[rootFolder] ?? 0
                    const selfInaccessible = (
                      !!g1 && g1.Count === 1 && (g2?.Count ?? 0) === 0 && n === 0 
                        && g1.ListData[0] === rootFolder)
                    return (
                      <Box key={rootFolder}>
                        <ListItem disablePadding
                          secondaryAction={!tabVal && (
                            selfInaccessible
                            ? <Typography color="error" variant="caption">Inaccessible Root Folder</Typography>
                            : <Box sx={{ '& > button': { ml: 0.5 } }}>
                                <Tooltip title="Inaccessible Folders">
                                  <IconButton>
                                    <Badge badgeContent={printNum(g1?.Count ?? 0)} color="error" max={999999} >
                                      <FolderOffIcon />
                                    </Badge>
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Inaccessible Files">
                                  <IconButton>
                                    <Badge badgeContent={printNum(g2?.Count ?? 0)} color="error" max={999999} >
                                      <ContentPasteOffIcon />
                                    </Badge>
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Files Scanned">
                                  <IconButton>
                                    <Badge badgeContent={printNum(n)} color="success" max={99999999} >
                                      {/* <DocumentScannerIcon /> */}
                                      <Files />
                                    </Badge>
                                  </IconButton>
                                </Tooltip>
                              </Box>)
                          }
                        >
                          <ListItemButton selected={!!tabVal} disabled={selfInaccessible}
                            onClick={() => {
                              setExpansion(v => {
                                if (v.dic[rootFolder]) {
                                  delete v.dic[rootFolder]
                                  return {...v}
                                }
                                return ({...v, dic: {...v.dic, [rootFolder]: 'folder' }})
                              })}
                            }
                          >
                            <ListItemText primary={
                              <Typography variant="body1" sx={{ color: '#ad5324', pr: 16.5 }}>{rootFolder}</Typography>
                            } />
                          </ListItemButton>
                        </ListItem>
                        {tabVal &&
                          <TabContext value={tabVal} >
                            <Stack direction="row">
                              <Box sx={{ mr: 'auto'}}>
                                <Tabs value={tabVal} 
                                  onChange={(e, value) => setExpansion(v => ({...v, dic: {...v.dic, [rootFolder]: value}}))}
                                >
                                  <Tab value="folder" 
                                    label={
                                      <Stack direction="row" alignItems="center" spacing={1}>
                                        <FolderOffIcon color={tabVal === 'folder' ? "primary" : "info"} fontSize="small" />
                                        <Typography variant="button" color={tabVal === 'folder' ? "primary" : "info"}>Inaccessible Folders</Typography>
                                        <Typography variant="caption" fontWeight="700" marginLeft={2}
                                          sx={{ px: 1, borderRadius: 10, minHeight: 20, bgcolor: 'error.main', color: theme => theme.palette.error.contrastText }}
                                        >
                                          {printNum(g1?.Count ?? 0)}
                                        </Typography>
                                      </Stack>  
                                    } 
                                  />
                                  <Tab value="file" 
                                    label={
                                      <Stack direction="row" alignItems="center" spacing={1}>
                                        <ContentPasteOffIcon color={tabVal === 'file' ? "primary" : "info"} fontSize="small" />
                                        <Typography variant="button" color={tabVal === 'file' ? "primary" : "info"}>Inaccessible Files</Typography>
                                        <Typography variant="caption" fontWeight="700" marginLeft={2}
                                          sx={{ px: 1, borderRadius: 10, minHeight: 20, bgcolor: 'error.main', color: theme => theme.palette.error.contrastText }}
                                        >
                                          {printNum(g2?.Count ?? 0)}
                                        </Typography>
                                      </Stack>  
                                    } 
                                  />
                                </Tabs>
                              </Box>
                              <WithSave 
                                render={({save, saving}) =>
                                  <Tooltip title="Request for Refresh">
                                    <Button variant="text" size="small" 
                                      sx={{  
                                        '& span.MuiButton-startIcon': { display: 'none' },
                                        '&:hover': {
                                          '& span.MuiButton-startIcon': { display: 'inherit' },
                                          '& > div .MuiSvgIcon-root': { opacity: 0.2 }
                                        }
                                      }}
                                      startIcon={<RefreshIcon />}
                                      disabled={!!states.lastRefreshUpdate}
                                      onClick={() => save({machine: data.machine}, {
                                        onSuccess: ({data}:any) => refresh(data?.data)
                                      })}
                                    >
                                      <Stack direction="row" alignItems="center" spacing={1}>
                                        {/* <DocumentScannerIcon color="success" fontSize="small" /> */}
                                        <Files color="success" fontSize="small" />
                                        <Typography variant="button" color="success">Files Scanned</Typography>
                                        <Typography variant="caption" fontWeight="700" marginLeft={2}
                                          sx={{ px: 1, borderRadius: 10, minHeight: 20, bgcolor: 'success.main', color: theme => theme.palette.success.contrastText }}
                                        >
                                          {printNum(n)}
                                        </Typography>
                                      </Stack>  
                                    </Button>
                                  </Tooltip>
                                }
                              />
                            </Stack>
                            <TabPanel value="folder">
                              <List sx={{ maxHeight: 720, overflowY: 'auto' }}>
                                {(g1?.ListData ?? []).map(s =>
                                  <ListItem>
                                    <Typography variant="body2">{s}</Typography>
                                  </ListItem>
                                )}
                              </List>
                              {(g1?.Count ?? 0) > 100 && 
                                <Typography variant="caption" color="InactiveCaptionText">Size of the list is limited to 100</Typography>}
                            </TabPanel>
                            <TabPanel value="file">
                              <List sx={{ maxHeight: 720, overflowY: 'auto' }}>
                                {(g2?.ListData ?? []).map(s =>
                                  <ListItem>
                                    <Typography variant="body2">{s}</Typography>
                                  </ListItem>
                                )}
                              </List>
                              {(g2?.Count ?? 0) > 100 && 
                                <Typography variant="caption" color="InactiveCaptionText">Size of the list is limited to 100</Typography>}
                            </TabPanel>
                            <Divider />
                          </TabContext>
                        }
                      </Box>
                    )
                  })}
                </List>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

    </Content>
  )
}

export default DMRootFolderScanInfo;


interface GroupInfo {
  Count: number;
  ListData: string[];
}

const printNum = (x: number) => {
  return x && numeral(x).format('0,0')
}